import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ActionButton,
  DrawerContext,
  FormControl,
  Input,
  Toaster,
} from '@notino/react-styleguide';

import messages from '@components/Universals/WishlistModifier/messages';
import { useWishlistLists } from '@components/Universals/WishlistModifier/useWishlistLists';
import { LIST_NAME_MAX_CHARS } from '@containers/WishlistLists/constants';
import {
  useListDataCache,
  useListsData,
} from '@containers/WishlistLists/hooks/useListsData';
import { trackCreateNewList } from '@containers/WishlistLists/tracking';
import { styled } from '@linaria/react';

const Wrapper = styled.div`
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
`;

const SaveButton = styled(ActionButton)`
  width: 100%;
  margin-top: 1.25rem;
`;

export const Content = () => {
  const [name, setName] = React.useState('');
  const { createList } = useWishlistLists();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const { formatMessage } = useIntl();
  const { closeDrawer } = DrawerContext.useDrawerContext();
  const { addNewListCache } = useListDataCache();
  const { lists } = useListsData();

  const onSave = async () => {
    setError(false);
    setLoading(true);
    trackCreateNewList({
      interaction: 'wishlist_new',
      wishlistTotalLists: lists.length + 1,
      wishlistTotalProducts: lists.reduce(
        (acc, currentList) => acc + (currentList.products?.length || 0),
        0
      ),
    });
    try {
      const list = await createList({ name: name.trim(), productIds: [] });
      addNewListCache(list);
      closeDrawer();
      Toaster.info({
        icon: 'IconRegularCheckmark',
        content: (
          <FormattedMessage
            {...messages.listCreated}
            values={{
              list: list.name,
              b: (chunks) => <b style={{ fontWeight: 500 }}>{chunks}</b>,
            }}
          />
        ),
      });
    } catch (e) {
      setError(true);
    }
    setLoading(false);
  };

  const trimmedName = name.trim();

  return (
    <Wrapper>
      <FormControl label={formatMessage(messages.listNameLabel)}>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus={true}
        />
      </FormControl>

      <Footer>
        <SaveButton
          onClick={onSave}
          isLoading={loading}
          error={error}
          disabled={!trimmedName || trimmedName.length > LIST_NAME_MAX_CHARS}
        >
          <FormattedMessage {...messages.createButton} />
        </SaveButton>
      </Footer>
    </Wrapper>
  );
};
